import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import api from '../services/api';
import ProductCard from '../components/product/ProductCard';

interface Banner { id: number; title: string; subtitle: string; image: string; button_text: string; button_link: string; text_color: string; }
interface Category { id: number; name: string; slug: string; image: string; }
interface Product { id: number; name: string; slug: string; price: number; compare_price?: number; featured_image: string; is_new?: boolean; is_featured?: boolean; colors?: any[]; sizes?: string[]; category?: any; }

export default function HomePage() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [newArrivals, setNewArrivals] = useState<Product[]>([]);
  const [featured, setFeatured] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState('tshirts');
  const [tabProducts, setTabProducts] = useState<Record<string, Product[]>>({});
  const bannerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    api.get('/banners/position/hero').then(r => setBanners(r.data)).catch(() => {});
    api.get('/categories/menu').then(r => setCategories(r.data)).catch(() => {});
    api.get('/products/new-arrivals').then(r => setNewArrivals(r.data)).catch(() => {});
    api.get('/products/featured').then(r => setFeatured(r.data)).catch(() => {});

    // Tab products - load men category
    api.get('/products?category=men&per_page=4').then(r => setTabProducts(prev => ({ ...prev, tshirts: r.data.data || [] }))).catch(() => {});
    api.get('/products?category=junior&per_page=4').then(r => setTabProducts(prev => ({ ...prev, boys: r.data.data || [] }))).catch(() => {});
    api.get('/products?category=women&per_page=4').then(r => setTabProducts(prev => ({ ...prev, women: r.data.data || [] }))).catch(() => {});
  }, []);

  useEffect(() => {
    if (banners.length > 1) {
      bannerRef.current = setInterval(() => setCurrentBanner(prev => (prev + 1) % banners.length), 5000);
      return () => { if (bannerRef.current) clearInterval(bannerRef.current); };
    }
  }, [banners.length]);

  const heroImages = [
    'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&q=80',
    'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1920&q=80',
    'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1920&q=80',
  ];

  const heroContent = [
    { title: 'New Collection 2026', subtitle: 'Elevate Your Style', cta: 'Shop Men', link: '/category/men' },
    { title: "Women's Collection", subtitle: 'Be Bold, Be You', cta: 'Shop Women', link: '/category/women' },
    { title: 'Summer Sale', subtitle: 'Up to 35% Off', cta: 'Shop Sale', link: '/special-prices' },
  ];

  const exploreCategories = [
    { name: 'MEN', slug: 'men', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&q=80' },
    { name: 'WOMEN', slug: 'women', image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=500&q=80' },
    { name: 'JUNIOR', slug: 'junior', image: 'https://images.unsplash.com/photo-1519238263530-99bdd11df2ea?w=500&q=80' },
  ];

  const tabs = [
    { key: 'tshirts', label: 'Tees Shirt' },
    { key: 'boys', label: 'Boys Vest' },
    { key: 'women', label: 'Women Warmer' },
  ];

  return (
    <div>
      {/* Hero Slider */}
      <section className="relative h-[60vh] md:h-[85vh] overflow-hidden">
        {heroImages.map((img, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === currentBanner ? 'opacity-100' : 'opacity-0'}`}
          >
            <img src={img} alt={`Banner ${i + 1}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-7xl mx-auto px-8 md:px-16">
                <div className={`transition-all duration-700 ${i === currentBanner ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
                  <p className="text-white/80 text-sm md:text-base font-medium tracking-widest uppercase mb-3">{heroContent[i]?.subtitle}</p>
                  <h1 className="text-4xl md:text-7xl font-serif text-white font-bold leading-tight mb-6">{heroContent[i]?.title}</h1>
                  <Link
                    to={heroContent[i]?.link || '/'}
                    className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-3.5 text-sm font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors"
                  >
                    {heroContent[i]?.cta} <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation */}
        {heroImages.length > 1 && (
          <>
            <button
              aria-label="Previous banner"
              onClick={() => setCurrentBanner(prev => (prev - 1 + heroImages.length) % heroImages.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/40 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              aria-label="Next banner"
              onClick={() => setCurrentBanner(prev => (prev + 1) % heroImages.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/40 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {heroImages.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Go to banner ${i + 1}`}
                  onClick={() => setCurrentBanner(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === currentBanner ? 'bg-white w-6' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* New Arrivals */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-gray-900">New Arrivals</h2>
          <div className="w-16 h-0.5 bg-gray-900 mx-auto mt-3" />
          <Link to="/new-in" className="inline-block mt-3 text-sm text-gray-500 underline underline-offset-2 hover:text-gray-900">View All</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {(newArrivals.length > 0 ? newArrivals : Array(8).fill(null)).slice(0, 8).map((product, i) =>
            product ? (
              <ProductCard key={product.id} product={product} />
            ) : (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-100 aspect-[3/4] w-full" />
                <div className="h-4 bg-gray-100 mt-3 w-3/4" />
                <div className="h-4 bg-gray-100 mt-2 w-1/2" />
              </div>
            )
          )}
        </div>
      </section>

      {/* What Would You Like to Explore */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-center text-2xl md:text-3xl font-serif font-semibold tracking-widest uppercase mb-12">What Would You Like To Explore?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {exploreCategories.map(cat => (
              <div key={cat.slug} className="group">
                <div className="relative overflow-hidden aspect-[3/4]">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
                </div>
                <div className="text-center mt-4">
                  <h3 className="text-lg font-serif font-medium text-gray-900 italic mb-3">{cat.name}</h3>
                  <Link to={`/category/${cat.slug}`} className="inline-block bg-gray-900 text-white px-8 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-gray-700 transition-colors">
                    Shop Now
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Banner */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=1920&q=80" alt="Brand" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="max-w-lg">
            <h2 className="text-6xl md:text-8xl font-serif font-bold text-white leading-none">BE<br/>YOU</h2>
            <p className="text-white/80 mt-4 text-lg">Express yourself through fashion. Discover our premium collection crafted for the modern Pakistani.</p>
            <Link to="/category/men" className="inline-flex items-center gap-2 mt-6 bg-white text-gray-900 px-8 py-3.5 text-sm font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors">
              Explore Now <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* More To Explore - Tabbed */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-gray-900 mb-4">More To Explore</h2>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {tabs.map((tab, i) => (
              <React.Fragment key={tab.key}>
                <button
                  onClick={() => setActiveTab(tab.key)}
                  className={`text-sm font-medium pb-1 border-b-2 transition-colors ${activeTab === tab.key ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                >
                  {tab.label}
                </button>
                {i < tabs.length - 1 && <span className="text-gray-300">/</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {(tabProducts[activeTab] || []).slice(0, 4).map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
            {(tabProducts[activeTab] || []).length === 0 && Array(4).fill(null).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-100 aspect-[3/4] w-full" />
                <div className="h-4 bg-gray-100 mt-3 w-3/4" />
                <div className="h-4 bg-gray-100 mt-2 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Men's Ethnic Wear */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-gray-900">Men's Ethnic Wear</h2>
            <div className="w-16 h-0.5 bg-gray-900 mx-auto mt-3" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {featured.slice(0, 8).map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/category/ethnic" className="inline-block border border-gray-900 text-gray-900 px-12 py-3.5 text-sm font-medium uppercase tracking-widest hover:bg-gray-900 hover:text-white transition-all">
              Show More
            </Link>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="py-12 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: '🚚', title: 'Free Delivery', desc: 'On orders above Rs. 2000' },
              { icon: '↩️', title: 'Easy Returns', desc: '7-day return policy' },
              { icon: '🔒', title: 'Secure Payment', desc: 'Cash on delivery available' },
              { icon: '💬', title: '24/7 Support', desc: 'WhatsApp & Phone support' },
            ].map((feature, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl mb-3">{feature.icon}</div>
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wider">{feature.title}</h4>
                <p className="text-gray-500 text-xs mt-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
